/**
 * 施術録 (treatment record) API service — clinic-wide rezept search + print.
 * Shared by A2 (はり・きゅう) and R2 (マッサージ) via X-Service-Type header.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type {
  TreatmentRecordPrintClinicWideInitData,
  TreatmentRecordPrintClinicWidePayload,
  TreatmentRecordSearchPayload,
  TreatmentRecordSearchResult,
} from '@/pages/main/treatmentRecord/types';

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export async function searchTreatmentRecords(
  siteType: string,
  clinicNumber: string,
  payload: TreatmentRecordSearchPayload,
  serviceType: string
): Promise<TreatmentRecordSearchResult> {
  const response = await apiClient.post<ApiEnvelope<TreatmentRecordSearchResult>>(
    clinicApiPath(siteType, clinicNumber, '/treatment-records/search'),
    payload,
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data.data;
}

export async function initTreatmentRecordPrintClinicWide(
  siteType: string,
  clinicNumber: string,
  rezeptIds: number[],
  serviceType: string
): Promise<TreatmentRecordPrintClinicWideInitData> {
  const response = await apiClient.post<ApiEnvelope<TreatmentRecordPrintClinicWideInitData>>(
    clinicApiPath(siteType, clinicNumber, '/treatment-records/print/init'),
    { rezept_ids: rezeptIds },
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data.data;
}

export async function outputTreatmentRecordPrintClinicWidePdf(
  siteType: string,
  clinicNumber: string,
  payload: TreatmentRecordPrintClinicWidePayload,
  serviceType: string
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/treatment-records/print/output'),
    payload,
    {
      headers: { 'X-Service-Type': serviceType },
      responseType: 'blob',
    }
  );
  return response.data as Blob;
}

export interface TreatmentRecordPrintClinicWideTicketResult {
  url: string;
}

/**
 * Request a preview URL for the clinic-wide 施術録 PDF. BE issues a short-lived
 * ticket and returns an unauthenticated preview URL; opening it in a new tab
 * lets the browser pick up the proper filename for Save-As (blob: URLs cannot).
 * No multi-HIC warning on this screen: 表面 is one page per rezept here, so each
 * 保険証 prints on its own page and there is nothing to choose between.
 * Spec: Docs/R2A2-treatment-record-front-spec.md §4.2
 */
export async function createTreatmentRecordPrintClinicWidePreviewTicket(
  siteType: string,
  clinicNumber: string,
  payload: TreatmentRecordPrintClinicWidePayload,
  serviceType: string
): Promise<TreatmentRecordPrintClinicWideTicketResult> {
  const response = await apiClient.post<
    ApiEnvelope<TreatmentRecordPrintClinicWideTicketResult>
  >(
    clinicApiPath(siteType, clinicNumber, '/treatment-records/print/preview-ticket'),
    payload,
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data.data;
}

